import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/cn.js';

/**
 * Heights come from the density variables, never from a literal, so switching
 * `data-density` resizes every control without touching a component.
 *
 * There is no focus ring here on purpose: `theme.css` gives every focusable
 * element the same `:focus-visible` outline, so a primitive that painted its own
 * would be the one inconsistent control on the screen.
 */
export const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-control font-medium ' +
    'transition-colors duration-[var(--duration-exit)] ease-standard ' +
    'disabled:pointer-events-none disabled:opacity-50 ' +
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-ink hover:bg-accent-hover',
        secondary: 'border border-border-strong bg-surface text-ink hover:bg-surface-sunken',
        subtle: 'bg-accent-soft text-accent-soft-ink hover:bg-accent-soft/70',
        ghost: 'text-ink-2 hover:bg-surface-sunken hover:text-ink',
        danger: 'bg-danger text-accent-ink hover:bg-danger/90',
        link: 'text-accent underline-offset-4 hover:underline',
      },
      size: {
        xs: 'h-[var(--control-height-xs)] px-2 text-secondary',
        sm: 'h-[var(--control-height-sm)] px-2.5 text-secondary',
        md: 'h-[var(--control-height)] px-3.5 text-body',
        lg: 'h-[calc(var(--control-height)+0.5rem)] px-5 text-body',
        icon: 'size-[var(--control-height)]',
        'icon-sm': 'size-[var(--control-height-sm)]',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  /** Render the child element instead of a `<button>` — for links that look like buttons. */
  asChild?: boolean;
}

export function Button({ className, variant, size, asChild, ...props }: ButtonProps) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}
